import * as React from "react";
import { MenuItem } from "primereact/menuitem";
import { ROUTES } from "./routes";
import { useContext, useMemo } from "react";
import { ClientId, makeClientId } from "./client-features";

export type ClientConfig = {
  isStrEnabled: boolean;
  isPropertyAssistantEnabled: boolean;
  isCasualtyAssistantEnabled: boolean;
  isRenewalEnabled: boolean;
  isDentalEnabled: boolean;
  isSl2Enabled: boolean;
  menuItems: MenuItem[];
  defaultRoute: string;
};

type Navigate = (route: string) => void;
type MakeMenuItem = (navigate: Navigate) => MenuItem;

const STR_MENU_ITEM: MakeMenuItem = (navigate) => ({
  id: "str",
  label: "STR Underwriter Assistant",
  icon: "pi pi-home",
  command: () => {
    navigate(ROUTES.STR);
  },
});

const PROPERTY_MENU_ITEM: MakeMenuItem = (navigate) => ({
  id: "property",
  label: "Property Dashboard",
  icon: "pi pi-building",
  command: () => {
    navigate(ROUTES.DASHBOARD_PROPERTY);
  },
});

const CASUALTY_MENU_ITEM: MakeMenuItem = (navigate) => ({
  id: "casualty",
  label: "Casualty Dashboard",
  icon: "pi pi-receipt",
  command: () => {
    navigate(ROUTES.DASHBOARD_CASUALTY);
  },
});

const RENEWAL_MENU_ITEM: MakeMenuItem = (navigate) => ({
  id: "renewal",
  label: "Renewal Assistant",
  icon: "pi pi-address-book",
  command: () => {
    navigate(ROUTES.RENEWAL);
  },
});

/*
const DENTAL_MENU_ITEM: MakeMenuItem = (navigate) => ({
  id: "dental",
  label: "Dental Assistant",
  icon: "pi pi-crown",
  command: () => {
    navigate(ROUTES.DENTAL);
  },
});*/

const SL2_MENU_ITEM: MakeMenuItem = (navigate) => ({
  id: "sl2",
  label: "SL-2 Verification",
  icon: "pi pi-file-check",
  command: () => {
    navigate(ROUTES.SL2);
  },
});

export const featuresForClientId = (
  clientId: string,
  navigate: Navigate
): ClientConfig => {
  switch (clientId) {
    case "proper":
      return {
        isStrEnabled: true,
        isPropertyAssistantEnabled: false,
        isCasualtyAssistantEnabled: false,
        isDentalEnabled: false,
        isRenewalEnabled: false,
        isSl2Enabled: true,
        menuItems: [STR_MENU_ITEM(navigate), SL2_MENU_ITEM(navigate)],
        defaultRoute: ROUTES.STR,
      };
    case "canopius":
      return {
        isStrEnabled: false,
        isPropertyAssistantEnabled: true,
        isCasualtyAssistantEnabled: false,
        isDentalEnabled: false,
        isRenewalEnabled: false,
        isSl2Enabled: false,
        menuItems: [PROPERTY_MENU_ITEM(navigate)],
        defaultRoute: ROUTES.DASHBOARD_PROPERTY,
      };
    case "pantheon":
      return {
        isStrEnabled: true,
        isPropertyAssistantEnabled: true,
        isCasualtyAssistantEnabled: true,
        isDentalEnabled: false,
        isRenewalEnabled: true,
        isSl2Enabled: true,
        menuItems: [
          STR_MENU_ITEM(navigate),
          PROPERTY_MENU_ITEM(navigate),
          CASUALTY_MENU_ITEM(navigate),
          RENEWAL_MENU_ITEM(navigate),
          SL2_MENU_ITEM(navigate),
        ],
        defaultRoute: ROUTES.DASHBOARD_PROPERTY,
      };
    case "intact":
      return {
        isStrEnabled: false,
        isPropertyAssistantEnabled: true,
        isCasualtyAssistantEnabled: true,
        isDentalEnabled: false,
        isRenewalEnabled: false,
        isSl2Enabled: false,
        menuItems: [],
        defaultRoute: ROUTES.DASHBOARD_PROPERTY,
      };
    case "norfolk_dedham":
      return {
        isStrEnabled: false,
        isPropertyAssistantEnabled: false,
        isCasualtyAssistantEnabled: false,
        isDentalEnabled: false,
        isRenewalEnabled: true,
        isSl2Enabled: false,
        menuItems: [],
        defaultRoute: ROUTES.RENEWAL,
      };
    case "dental":
      return {
        isStrEnabled: false,
        isPropertyAssistantEnabled: false,
        isCasualtyAssistantEnabled: false,
        isDentalEnabled: true,
        isRenewalEnabled: false,
        isSl2Enabled: false,
        menuItems: [],
        defaultRoute: ROUTES.DENTAL,
      };
    case "bluesky_dev":
    case "bluesky":
      return {
        isStrEnabled: false,
        isPropertyAssistantEnabled: true,
        isCasualtyAssistantEnabled: false,
        isDentalEnabled: false,
        isRenewalEnabled: false,
        isSl2Enabled: false,
        menuItems: [],
        defaultRoute: ROUTES.DASHBOARD_PROPERTY,
      };
    case "uip_dev":
    case "uip":
      return {
        isStrEnabled: false,
        isPropertyAssistantEnabled: true,
        isCasualtyAssistantEnabled: false,
        isDentalEnabled: false,
        isRenewalEnabled: false,
        isSl2Enabled: false,
        menuItems: [],
        defaultRoute: ROUTES.DASHBOARD_PROPERTY,
      };
  }

  throw new Error(`unknown clientId ${clientId}`);
};

export const useFeaturesForClientId = (
  clientId: string,
  navigate: Navigate
): ClientConfig => {
  return useMemo(
    () => featuresForClientId(clientId, navigate),
    [clientId, navigate]
  );
};

export const ClientIdContext = React.createContext<string | null>(null);
export const useClientId = (): ClientId => {
  const clientId = useContext(ClientIdContext);
  if (!clientId) {
    throw new Error("Client ID Context not provided");
  }
  return makeClientId(clientId);
};
